import { useCallback, useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import { useDebounce } from './useDebounce'
import { DEFAULT_PAGE_SIZE } from '@/lib/listFetch'

// URL-backed controller for a FULL-PAGE server-paginated list. The URL query
// string is the SINGLE source of truth: page / limit / search / filters are
// read FROM the URL, mutating any of them rewrites the URL, and the URL change
// is what drives the data fetch. This makes list views shareable, bookmarkable,
// and survive a refresh / back-forward.
//
// Drop-in API match for `useListParams` (which keeps state local) - use THIS
// one for top-level index screens, and the local one for embedded lists
// (detail-page tabs, settings panels, entity pickers) that must not pollute or
// collide on the page URL.
const RESERVED = new Set([
  'page',
  'limit',
  'search',
  'view',
  'panel',
  'payment',
  'detail_status',
  'create_client_id',
  'create_job_id',
  'create_amount',
  'report_job',
])

export function useUrlListParams<F extends Record<string, unknown>>(
  defaultFilters: F = {} as F
) {
  const [searchParams, setSearchParams] = useSearchParams()
  // Defaults are captured once (lazy init); they seed filter values that are
  // not present in the URL. Undefined entries are dropped - they only served to
  // hint a key and would never become a real query value.
  const [seededDefaults] = useState(() => {
    const out: Record<string, unknown> = {}
    for (const [key, value] of Object.entries(defaultFilters)) {
      if (value !== undefined) out[key] = value
    }
    return out
  })

  const page = Math.max(1, Number(searchParams.get('page')) || 1)
  const limit = Number(searchParams.get('limit')) || DEFAULT_PAGE_SIZE
  const urlSearch = searchParams.get('search') ?? ''

  // Merge param changes into the URL. Empty / undefined values are removed so
  // the URL stays clean (e.g. "all statuses" drops `status` entirely).
  // Discrete actions (pagination, page-size, filter selects) PUSH a history
  // entry so Back undoes them one step at a time; the debounced search PASSES
  // `replace` so typing doesn't spam one back-stack entry per keystroke.
  const patch = useCallback(
    (
      changes: Record<string, unknown>,
      { resetPage = false, replace = false } = {}
    ) => {
      setSearchParams(
        prev => {
          const next = new URLSearchParams(prev)
          if (resetPage) next.delete('page')
          for (const [key, value] of Object.entries(changes)) {
            if (value === undefined || value === null || value === '')
              next.delete(key)
            else next.set(key, String(value))
          }
          return next
        },
        { replace }
      )
    },
    [setSearchParams]
  )

  const setPage = useCallback(
    (p: number) => patch({ page: p <= 1 ? undefined : p }),
    [patch]
  )
  const setLimit = useCallback(
    (l: number) =>
      patch({
        limit: l === DEFAULT_PAGE_SIZE ? undefined : l,
        page: undefined,
      }),
    [patch]
  )

  // The search input is debounced before it touches the URL (avoids a history
  // write per keystroke). The input keeps an ephemeral local buffer; the URL
  // remains the source of truth that drives the fetch.
  const [search, setSearch] = useState(urlSearch)
  const debouncedSearch = useDebounce(search, 300)

  useEffect(() => {
    if (debouncedSearch !== urlSearch)
      patch(
        { search: debouncedSearch || undefined },
        {
          resetPage: true,
          replace: true,
        }
      )
  }, [debouncedSearch, urlSearch, patch])

  // Keep the input buffer in sync when the URL changes externally (back/forward
  // navigation, a seeded link). Setting an equal value is a no-op, so no loop.
  useEffect(() => {
    // eslint-disable-next-line react-hooks/set-state-in-effect
    setSearch(urlSearch)
  }, [urlSearch])

  const onSearchChange = useCallback((value: string) => setSearch(value), [])

  // Filters = every non-reserved URL param, seeded by the supplied defaults.
  const filters = useMemo(() => {
    const out: Record<string, unknown> = { ...seededDefaults }
    for (const [key, value] of searchParams.entries()) {
      if (!RESERVED.has(key)) out[key] = value
    }
    return out as F
  }, [searchParams, seededDefaults])

  const setFilters = useCallback(
    (next: Partial<F>) => patch(next, { resetPage: true }),
    [patch]
  )

  const query = useMemo(
    () => ({
      page,
      limit,
      search: urlSearch || undefined,
      ...filters,
    }),
    [page, limit, urlSearch, filters]
  )

  return {
    page,
    setPage,
    limit,
    setLimit,
    search,
    onSearchChange,
    filters,
    setFilters,
    query,
  }
}
